import { cn } from "@nexus/ui";
import type { KeyboardEvent, PointerEvent, TouchEvent } from "react";

/**
 * Column-edge separator shared by the Opportunities table and the Opportunity Hub.
 * Callers own the width math so the old table can stay min-only while the Hub clamps to a max.
 * Fine-only pointers get a 12px edge. Any coarse pointer, including a touch screen on a
 * hybrid whose primary pointer is a mouse, keeps a 44px target inside the header.
 */
const FINE_POINTER_HIT_CLASS = "w-3 min-w-3 max-w-3";
const COARSE_POINTER_HIT_CLASS =
  "any-pointer-coarse:w-11 any-pointer-coarse:min-w-11 any-pointer-coarse:max-w-11";

export function ColumnResizeHandle({
  label,
  valueNow,
  valueMin,
  valueMax,
  valueText,
  isActive = false,
  onPointerDown,
  onTouchStart,
  onDoubleClick,
  onKeyDown,
}: {
  label: string;
  valueNow?: number;
  valueMin?: number;
  valueMax?: number;
  /** Spoken width when the column has no maximum. Omits the numeric range so AT does not assume 100. */
  valueText?: string;
  isActive?: boolean;
  onPointerDown: (event: PointerEvent<HTMLDivElement>) => void;
  onTouchStart: (event: TouchEvent<HTMLDivElement>) => void;
  onDoubleClick: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
}) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={valueMax != null && valueText == null ? valueNow : undefined}
      aria-valuemin={valueMax != null && valueText == null ? valueMin : undefined}
      aria-valuemax={valueMax != null && valueText == null ? valueMax : undefined}
      aria-valuetext={valueMax != null && valueText == null ? undefined : valueText}
      tabIndex={0}
      data-resize-handle
      onPointerDown={(event) => {
        event.stopPropagation();
        onPointerDown(event);
      }}
      onTouchStart={(event) => {
        event.stopPropagation();
        onTouchStart(event);
      }}
      onDoubleClick={(event) => {
        event.stopPropagation();
        onDoubleClick();
      }}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={onKeyDown}
      className={cn(
        // Stay inside the owning header. Height follows the header; a 44px min-height covers the first row.
        "absolute bottom-0 right-0 top-0 z-20 cursor-col-resize select-none touch-none outline-none focus-visible:ring-2 focus-visible:ring-ring group/resize",
        FINE_POINTER_HIT_CLASS,
        COARSE_POINTER_HIT_CLASS,
      )}
    >
      <div
        className={cn(
          "absolute right-0 top-0 h-full w-0.5 rounded-full transition-colors",
          isActive
            ? "bg-primary"
            : "bg-transparent group-hover/resize:bg-border group-focus-visible/resize:bg-border",
        )}
      />
    </div>
  );
}
